import i18next from 'i18next';
import { ReactComponent as PendingIcon } from 'assets/icons/pending.svg';
import { ReactComponent as ShippedIcon } from 'assets/icons/shipped.svg';
import { ReactComponent as ProcessingIcon } from 'assets/icons/processing.svg';
import { ReactComponent as HoldIcon } from 'assets/icons/hold.svg';
// TODO(Phase 7): drop when MUI VisibilityOff has a @shared/ui wrapper.
// eslint-disable-next-line no-restricted-imports
import { VisibilityOff as VisibilityOffIcon, Cancel as DeclinedIcon } from 'mui/icons';

export const ASSESSMENT_STATUSES = {
  invited: 'invited',
  created: 'created',
  // Set server-side when the patient first opens their assessment link
  // (after DOB verification, before accepting terms). Sits between
  // invited/created and inProgress. Shown to users as "Opened" — the
  // raw enum name is never surfaced.
  staled: 'staled',
  inProgress: 'inProgress',
  pendingReview: 'pendingReview',
  partialProcessed: 'partialProcessed',
  completed: 'completed',
  processing: 'processing',
  scheduled: 'scheduled',
  eTransfer: 'eTransfer',
  canceled: 'canceled',
  noShow: 'noShow',
};

export const ASSESSMENT_STATUSES_OPTIONS = [
  {
    label: i18next.t('tables:invited'),
    value: ASSESSMENT_STATUSES.invited,
    icon: HoldIcon,
    enabledForNonSponsors: false,
  },
  {
    // "Instant Created" — the status exists only for instant
    // assessments (created in clinic, not started yet); plain
    // "Created" confused users into reading it as a generic
    // lifecycle step (customer request 2026-08-24).
    label: i18next.t('tables:instantCreated'),
    value: ASSESSMENT_STATUSES.created,
    icon: HoldIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:staled'),
    value: ASSESSMENT_STATUSES.staled,
    icon: HoldIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:inProgress'),
    value: ASSESSMENT_STATUSES.inProgress,
    icon: PendingIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:scheduled'),
    value: ASSESSMENT_STATUSES.scheduled,
    icon: ProcessingIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:pendingReview'),
    value: ASSESSMENT_STATUSES.pendingReview,
    icon: ProcessingIcon,
    enabledForNonSponsors: true,
    addDivider: true,
  },
  {
    label: i18next.t('tables:partialProcessed'),
    value: ASSESSMENT_STATUSES.partialProcessed,
    icon: ProcessingIcon,
    enabledForNonSponsors: true,
    addDivider: true,
  },
  {
    label: i18next.t('tables:completed'),
    value: ASSESSMENT_STATUSES.completed,
    icon: ShippedIcon,
    enabledForNonSponsors: true,
  },
  {
    label: i18next.t('tables:processing'),
    value: ASSESSMENT_STATUSES.processing,
    icon: ShippedIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:eTransfer'),
    value: ASSESSMENT_STATUSES.eTransfer,
    icon: ShippedIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:canceled'),
    value: ASSESSMENT_STATUSES.canceled,
    icon: ShippedIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:noShow'),
    value: ASSESSMENT_STATUSES.noShow,
    icon: VisibilityOffIcon,
    enabledForNonSponsors: false,
  },
];

// Filter-dropdown subset of ASSESSMENT_STATUSES_OPTIONS. Excludes
// `noShow` because the assessments table now has a dedicated
// `noShowStatus` column for that flag — keeping it in the Status
// filter would duplicate the control. The full options array is
// still exported for label lookups (CSV export, row actions,
// bot-activity table, legacy consumers) where historical
// `status === 'noShow'` rows must still render with a label.
//
// Ordered by lifecycle (customer request 2026-08-24): not started →
// in flight → processing → final, Canceled last. The status column
// opts out of SelectFilter's generic badge-tone sorting via
// `preserveOptionOrder` so this order is what the user sees.
const STATUS_FILTER_LIFECYCLE_ORDER = [
  ASSESSMENT_STATUSES.created,
  ASSESSMENT_STATUSES.invited,
  ASSESSMENT_STATUSES.scheduled,
  ASSESSMENT_STATUSES.staled,
  ASSESSMENT_STATUSES.inProgress,
  ASSESSMENT_STATUSES.partialProcessed,
  ASSESSMENT_STATUSES.processing,
  ASSESSMENT_STATUSES.pendingReview,
  ASSESSMENT_STATUSES.completed,
  ASSESSMENT_STATUSES.eTransfer,
  ASSESSMENT_STATUSES.canceled,
];

export const ASSESSMENT_STATUSES_FILTER_OPTIONS = STATUS_FILTER_LIFECYCLE_ORDER.map(value =>
  ASSESSMENT_STATUSES_OPTIONS.find(option => option.value === value)
);

// Bot-activities status filter: same lifecycle order, plus the legacy
// `noShow` status at the end. Unlike the assessments table, bot
// activities has no dedicated `noShowStatus` column, and historical
// rows still carry `status === 'noShow'` — dropping the option there
// would make those rows unfilterable.
export const ASSESSMENT_STATUSES_FILTER_OPTIONS_WITH_NO_SHOW = [
  ...ASSESSMENT_STATUSES_FILTER_OPTIONS,
  ASSESSMENT_STATUSES_OPTIONS.find(option => option.value === ASSESSMENT_STATUSES.noShow),
];

// Separate backend column added alongside `status`. Tracks whether
// the patient was marked as a no-show independently of the
// assessment's lifecycle status. Two values: `none` (default — not
// flagged) and `noShow`.
// Mutually exclusive states of the backend `noShowStatus` column —
// a survey is none / noShow / declined, never two of those at once.
export const NO_SHOW_STATUSES = {
  none: 'none',
  noShow: 'noShow',
  declined: 'declined',
};

export const NO_SHOW_STATUSES_OPTIONS = [
  {
    label: i18next.t('tables:notUpdated'),
    value: NO_SHOW_STATUSES.none,
  },
  {
    label: i18next.t('tables:noShow'),
    value: NO_SHOW_STATUSES.noShow,
    icon: VisibilityOffIcon,
  },
  {
    label: i18next.t('tables:declined'),
    value: NO_SHOW_STATUSES.declined,
    icon: DeclinedIcon,
  },
];

export const INVITATION_TYPE = {
  send: 'send',
  resend: 'resend',
  notAllowed: 'notAllowed',
};
